import type { FragmentType } from 'src/gql';
import { useMemo } from 'react';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@savvy-kit/components/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { Info } from 'lucide-react';
import { graphql, unmaskFragment } from 'src/gql';

import { CURRENT_TAX_YEAR } from '../tax-utils';
import { TaxHistoryRow } from './tax-history-row';

export const TaxHistoryTableFragment = graphql(/* GraphQL */ `
  fragment TaxHistoryTableFragment on TaxPageClient {
    userFullName
    taxTranscripts {
      id
      taxYear
      totalIncome
      federalTaxLiability
      federalRefund
      totalDeductions
      yoyChange {
        totalIncomePct
        federalTaxLiabilityPct
        federalRefundPct
        totalDeductionsPct
      }
    }
  }
`);

type Props = {
  readonly clientData: FragmentType<typeof TaxHistoryTableFragment> | undefined;
};

export function TaxHistoryTable({ clientData }: Props) {
  const data = unmaskFragment(TaxHistoryTableFragment, clientData);

  const sortedTranscripts = useMemo(() => {
    const transcripts = data?.taxTranscripts ?? [];
    const sorted = [...transcripts].sort((a, b) => a.taxYear - b.taxYear);

    // Add current tax year placeholder if transcripts exist but current year doesn't
    const hasCurrentYear = sorted.some((t) => t.taxYear === CURRENT_TAX_YEAR);
    if (sorted.length > 0 && !hasCurrentYear) {
      sorted.push({
        id: `placeholder-${CURRENT_TAX_YEAR}`,
        taxYear: CURRENT_TAX_YEAR,
        totalIncome: null,
        federalTaxLiability: null,
        federalRefund: null,
        totalDeductions: null,
        yoyChange: null,
      });
    }

    return sorted;
  }, [data?.taxTranscripts]);

  if (sortedTranscripts.length === 0) return null;

  return (
    <div className="w-full pr-31">
      <Table borderless className="w-full border-collapse [&_td]:p-4">
        <TableHeader className="bg-white">
          <TableRow className="border-b border-slate-200 hover:bg-transparent text-secondary text-xs ">
            <TableHead className="uppercase align-middle font-semibold">TYPE</TableHead>
            {sortedTranscripts.map((t) => (
              <TableHead key={t.id} className="text-right font-semibold min-w-[120px]">
                <div className="flex items-center justify-end gap-1.5">
                  {t.taxYear}
                  {t.taxYear !== CURRENT_TAX_YEAR && (
                    <Tooltip>
                      <TooltipTrigger render={<Info className="h-4 w-4 text-slate-400" />} />
                      <TooltipContent>
                        This tax year's data was provided by the IRS and may have some incomplete information.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          <TaxHistoryRow
            label="Gross income"
            dotClassName="bg-chart-1-dark"
            transcripts={sortedTranscripts}
            getValue={(t) => t.totalIncome}
            getPct={(t) => t.yoyChange?.totalIncomePct}
          />
          <TaxHistoryRow
            label="Federal tax"
            dotClassName="bg-chart-2-light"
            transcripts={sortedTranscripts}
            getValue={(t) => t.federalTaxLiability}
            getPct={(t) => t.yoyChange?.federalTaxLiabilityPct}
          />
          <TaxHistoryRow
            label="Deductions"
            dotClassName="bg-chart-4-light"
            transcripts={sortedTranscripts}
            getValue={(t) => t.totalDeductions}
            getPct={(t) => t.yoyChange?.totalDeductionsPct}
          />
          <TaxHistoryRow
            label="Federal refund/owed"
            dotClassName="bg-chart-3-dark"
            transcripts={sortedTranscripts}
            getValue={(t) => t.federalRefund}
            getPct={(t) => t.yoyChange?.federalRefundPct}
            shouldColorValue
            hidePct
          />
        </TableBody>
      </Table>
    </div>
  );
}
